import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './AuthIntroAnimation.css';

// Both paths are 4 cubic Beziers + Z, so framer-motion can interpolate the `d`
// attribute smoothly between circle and 4-point rounded star.
const CIRCLE_PATH =
  'M60 38 C 78 38 82 42 82 60 C 82 78 78 82 60 82 C 42 82 38 78 38 60 C 38 42 42 38 60 38 Z';
const STAR_PATH =
  'M60 6 C 64 42 78 56 114 60 C 78 64 64 78 60 114 C 56 78 42 64 6 60 C 42 56 56 42 60 6 Z';

const AuthIntroAnimation = ({ onComplete }) => {
  // floating -> converging -> morphing -> expanding -> done
  const [phase, setPhase] = useState('floating');

  useEffect(() => {
    const timeline = [
      { t: 1300, p: 'converging' },
      { t: 1900, p: 'morphing' },
      { t: 2700, p: 'expanding' },
      { t: 3500, p: 'done' },
    ];
    const timers = timeline.map(({ t, p }) =>
      setTimeout(() => {
        setPhase(p);
        if (p === 'done') {
          onComplete && onComplete();
        }
      }, t)
    );
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  const showDots = phase === 'floating' || phase === 'converging';
  const showStar = phase === 'morphing' || phase === 'expanding';

  return (
    <AnimatePresence>
      {phase !== 'done' && (
        <motion.div
          className="auth-intro"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
        >
          <motion.div
            className="auth-intro-bg"
            initial={{ opacity: 1 }}
            animate={{ opacity: phase === 'expanding' ? 0.55 : 1 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          />

          <AnimatePresence>
            {phase === 'expanding' && (
              <motion.div
                key="overlay"
                className="auth-intro-overlay"
                initial={{ scale: 0, opacity: 0.9, borderRadius: '50%' }}
                animate={{ scale: 5, opacity: 0.35, borderRadius: '0%' }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.85, ease: [0.65, 0, 0.35, 1] }}
              />
            )}
          </AnimatePresence>

          <div className="auth-intro-stage">
            <svg
              width="280"
              height="280"
              viewBox="0 0 120 120"
              className="auth-intro-svg"
              style={{ overflow: 'visible' }}
            >
              <defs>
                <filter
                  id="auth-intro-glow"
                  x="-100%"
                  y="-100%"
                  width="300%"
                  height="300%"
                >
                  <feGaussianBlur stdDeviation="3.5" result="b" />
                  <feMerge>
                    <feMergeNode in="b" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <radialGradient id="dot-grad" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#ffffff" />
                  <stop offset="100%" stopColor="#f0e6ff" />
                </radialGradient>
              </defs>

              <AnimatePresence>
                {showDots && (
                  <>
                    <motion.circle
                      key="dot-top"
                      cx="60"
                      r="9"
                      fill="url(#dot-grad)"
                      filter="url(#auth-intro-glow)"
                      initial={{ cy: 60, opacity: 0, scale: 0.4 }}
                      animate={
                        phase === 'floating'
                          ? {
                              cy: [26, 32, 26],
                              opacity: 1,
                              scale: 1,
                            }
                          : {
                              cy: 60,
                              opacity: 1,
                              scale: 1.2,
                            }
                      }
                      exit={{ opacity: 0, scale: 1.6 }}
                      transition={
                        phase === 'floating'
                          ? {
                              cy: {
                                duration: 1.6,
                                repeat: Infinity,
                                ease: 'easeInOut',
                              },
                              opacity: { duration: 0.4 },
                              scale: { duration: 0.4 },
                            }
                          : {
                              cy: { duration: 0.5, ease: [0.16, 0.84, 0.3, 1] },
                              scale: { duration: 0.5 },
                            }
                      }
                    />
                    <motion.circle
                      key="dot-bottom"
                      cx="60"
                      r="9"
                      fill="url(#dot-grad)"
                      filter="url(#auth-intro-glow)"
                      initial={{ cy: 60, opacity: 0, scale: 0.4 }}
                      animate={
                        phase === 'floating'
                          ? {
                              cy: [94, 88, 94],
                              opacity: 1,
                              scale: 1,
                            }
                          : {
                              cy: 60,
                              opacity: 1,
                              scale: 1.2,
                            }
                      }
                      exit={{ opacity: 0, scale: 1.6 }}
                      transition={
                        phase === 'floating'
                          ? {
                              cy: {
                                duration: 1.6,
                                repeat: Infinity,
                                ease: 'easeInOut',
                              },
                              opacity: { duration: 0.4 },
                              scale: { duration: 0.4 },
                            }
                          : {
                              cy: { duration: 0.5, ease: [0.16, 0.84, 0.3, 1] },
                              scale: { duration: 0.5 },
                            }
                      }
                    />
                  </>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {showStar && (
                  <motion.path
                    key="morph"
                    fill="#ffffff"
                    filter="url(#auth-intro-glow)"
                    initial={{ d: CIRCLE_PATH, scale: 1, rotate: 0, opacity: 0 }}
                    animate={{
                      d: STAR_PATH,
                      scale: phase === 'expanding' ? 0.7 : 1,
                      rotate: phase === 'expanding' ? -45 : 0,
                      opacity: 1,
                    }}
                    exit={{ opacity: 0, scale: 0.5 }}
                    style={{ transformOrigin: '60px 60px' }}
                    transition={{
                      d: { duration: 0.7, ease: [0.65, 0, 0.35, 1] },
                      scale: { duration: 0.7, ease: 'easeOut' },
                      rotate: { duration: 0.7, ease: 'easeOut' },
                      opacity: { duration: 0.25 },
                    }}
                  />
                )}
              </AnimatePresence>
            </svg>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AuthIntroAnimation;
